import React from 'react';
import { Language } from '../../pages/DevPortal';
import { Server, ArrowRight, CheckCircle2, AlertTriangle, Copy, Terminal, Zap, Shield, RefreshCw } from 'lucide-react';

function CodeBlock({ children, title }: { children: string; title?: string }) {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(children);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative group rounded-lg overflow-hidden border border-slate-200 my-4">
            {title && (
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</span>
                    <button
                        onClick={handleCopy}
                        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-600 transition-colors px-2 py-1 rounded hover:bg-indigo-50"
                    >
                        {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>
            )}
            <pre className="bg-slate-900 p-4 text-xs text-slate-300 font-mono overflow-x-auto leading-relaxed whitespace-pre-wrap">
                {children}
            </pre>
        </div>
    );
}

export default function TasyGuideSection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://hospital-status.com';

    return (
        <div className="animate-fade-in">
            <div className="flex items-center gap-3 mb-2">
                <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {isEn ? 'Integration Guide' : 'Guía de Integración'}
                </span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {isEn ? 'Philips TASY EMR Integration' : 'Integración con Philips TASY EMR'}
            </h1>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                {isEn
                    ? 'This guide walks your IT team through connecting Philips TASY EMR to Hospital Status. When a surgery is scheduled in TASY, the patient automatically appears on the waiting room displays — and when the surgery ends, timestamps flow back to the clinical record.'
                    : 'Esta guía lleva a tu equipo de TI paso a paso para conectar Philips TASY EMR con Hospital Status. Cuando se programa una cirugía en TASY, el paciente aparece automáticamente en las pantallas de sala de espera — y al terminar, los timestamps regresan al expediente clínico.'}
            </p>

            {/* ─── PREREQUISITES ─────────────────────────────── */}
            <section className="mb-14">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-amber-100 text-amber-700 p-2 rounded-lg"><Shield className="w-5 h-5" /></span>
                    {isEn ? 'Prerequisites' : 'Requisitos Previos'}
                </h2>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                    <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            <h4 className="font-bold text-amber-900 text-sm mb-2">
                                {isEn ? 'Before you begin' : 'Antes de comenzar'}
                            </h4>
                            <ul className="text-sm text-amber-800 space-y-2">
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                    <span>{isEn
                                        ? <><strong>Integration Engine</strong> — Mirth Connect, Iguana, or any HL7-capable middleware running inside your hospital network.</>
                                        : <><strong>Motor de Integración</strong> — Mirth Connect, Iguana, o cualquier middleware HL7 corriendo dentro de la red del hospital.</>}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                    <span>{isEn
                                        ? <><strong>Outbound Network Access</strong> — The middleware server must reach <code className="bg-amber-100 px-1 rounded text-xs">{baseUrl}/api/integration/v1/*</code> via HTTPS (port 443).</>
                                        : <><strong>Acceso de Red Saliente</strong> — El servidor middleware debe alcanzar <code className="bg-amber-100 px-1 rounded text-xs">{baseUrl}/api/integration/v1/*</code> por HTTPS (puerto 443).</>}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                    <span>{isEn
                                        ? <><strong>API Key</strong> — Generated from the Hospital Status Admin Panel → Integrations module. You'll receive a Key ID and a Secret (shown only once).</>
                                        : <><strong>API Key</strong> — Generada desde el Panel Administrativo de Hospital Status → módulo Integraciones. Recibirás un Key ID y un Secret (se muestra solo una vez).</>}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                    <span>{isEn
                                        ? <><strong>Room Mapping</strong> — A table mapping TASY location codes (PV1-3) to Hospital Status Room IDs. Your Hospital Status admin can provide the Room IDs from the dashboard.</>
                                        : <><strong>Mapeo de Quirófanos</strong> — Una tabla que mapee los códigos de ubicación de TASY (PV1-3) a los Room IDs de Hospital Status. Tu administrador puede darte los IDs desde el dashboard.</>}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── ARCHITECTURE DIAGRAM ─────────────────────── */}
            <section className="mb-14">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-indigo-100 text-indigo-700 p-2 rounded-lg"><Server className="w-5 h-5" /></span>
                    {isEn ? 'Architecture Overview' : 'Arquitectura General'}
                </h2>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 font-mono text-xs leading-loose text-slate-600 overflow-x-auto">
                    <pre className="whitespace-pre">{`
┌─────────────────────────────────────────────────────────────────────┐
│                    HOSPITAL INTERNAL NETWORK                        │
│                                                                     │
│  ┌──────────┐    HL7 ADT     ┌──────────────┐    HTTPS/JSON       │
│  │  TASY    │ ──────────────▶│  MIDDLEWARE   │ ─────────────────▶  │ ─── INTERNET ──▶  Hospital Status API
│  │  EMR     │                │  (Mirth /     │                     │                   (Cloudflare Edge)
│  │          │ ◀──────────────│   Node.js)    │ ◀─────────────────  │ ◀── INTERNET ───  Webhook POST
│  └──────────┘    Update EHR  └──────────────┘    Signed JSON       │
│       │                           │                                 │
│   Oracle DB                  Logs & Retry                          │
└─────────────────────────────────────────────────────────────────────┘
                    `}</pre>
                </div>
                <p className="text-sm text-slate-500 mt-3 italic">
                    {isEn
                        ? '* The middleware translates between HL7 v2 (TASY\'s language) and REST/JSON (Hospital Status\'s language). It runs inside your network and never exposes TASY to the internet.'
                        : '* El middleware traduce entre HL7 v2 (el idioma de TASY) y REST/JSON (el idioma de Hospital Status). Corre dentro de tu red y nunca expone TASY al internet.'}
                </p>
            </section>

            {/* ─── EVENT MAPPING TABLE ───────────────────────── */}
            <section className="mb-14">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-emerald-100 text-emerald-700 p-2 rounded-lg"><ArrowRight className="w-5 h-5" /></span>
                    {isEn ? 'Event Mapping: TASY → Hospital Status' : 'Mapeo de Eventos: TASY → Hospital Status'}
                </h2>

                <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'TASY Event' : 'Evento en TASY'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'HL7 Message' : 'Mensaje HL7'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'API Action' : 'Acción API'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-600">
                            <tr><td className="px-4 py-2.5">{isEn ? 'Patient admitted for surgery' : 'Paciente admitido a cirugía'}</td><td className="px-4 py-2.5 font-mono text-xs">ADT^A01</td><td className="px-4 py-2.5 font-mono text-xs text-emerald-700">POST /v1/patients</td><td className="px-4 py-2.5 font-mono text-xs">WAITING</td></tr>
                            <tr><td className="px-4 py-2.5">{isEn ? 'Transfer to pre-op' : 'Traslado a pre-operatorio'}</td><td className="px-4 py-2.5 font-mono text-xs">ADT^A02</td><td className="px-4 py-2.5 font-mono text-xs text-amber-700">PUT /v1/patients/:id/status</td><td className="px-4 py-2.5 font-mono text-xs">PREPARATION</td></tr>
                            <tr><td className="px-4 py-2.5">{isEn ? 'Enter OR' : 'Ingreso a quirófano'}</td><td className="px-4 py-2.5 font-mono text-xs">ADT^A02</td><td className="px-4 py-2.5 font-mono text-xs text-amber-700">PUT /v1/patients/:id/status</td><td className="px-4 py-2.5 font-mono text-xs">ROOM_ENTRY</td></tr>
                            <tr className="bg-indigo-50/50"><td className="px-4 py-2.5 font-medium text-indigo-900">{isEn ? 'Anesthesia start' : 'Inicio de anestesia'}</td><td className="px-4 py-2.5 font-mono text-xs">PEPC Event</td><td className="px-4 py-2.5 font-mono text-xs text-amber-700">PUT /v1/patients/:id/status</td><td className="px-4 py-2.5 font-mono text-xs font-bold text-indigo-700">ANESTHESIA</td></tr>
                            <tr className="bg-indigo-50/50"><td className="px-4 py-2.5 font-medium text-indigo-900">{isEn ? 'Surgery start (incision)' : 'Inicio de cirugía (incisión)'}</td><td className="px-4 py-2.5 font-mono text-xs">PEPC Event</td><td className="px-4 py-2.5 font-mono text-xs text-amber-700">PUT /v1/patients/:id/status</td><td className="px-4 py-2.5 font-mono text-xs font-bold text-indigo-700">SURGERY</td></tr>
                            <tr><td className="px-4 py-2.5">{isEn ? 'Transfer to recovery' : 'Traslado a recuperación'}</td><td className="px-4 py-2.5 font-mono text-xs">ADT^A02</td><td className="px-4 py-2.5 font-mono text-xs text-amber-700">PUT /v1/patients/:id/status</td><td className="px-4 py-2.5 font-mono text-xs">RECOVERY</td></tr>
                            <tr><td className="px-4 py-2.5">{isEn ? 'Patient discharged' : 'Alta del paciente'}</td><td className="px-4 py-2.5 font-mono text-xs">ADT^A03</td><td className="px-4 py-2.5 font-mono text-xs text-amber-700">PUT /v1/patients/:id/status</td><td className="px-4 py-2.5 font-mono text-xs">EXIT</td></tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-slate-400 mt-3 italic">
                    {isEn
                        ? '* PEPC Events (Anesthesia, Surgery) originate from TASY\'s Electronic Perioperative Patient Chart module. ADT messages are standard HL7 v2.'
                        : '* Los eventos PEPC (Anestesia, Cirugía) se originan del módulo de Carta Perioperatoria Electrónica de TASY. Los mensajes ADT son HL7 v2 estándar.'}
                </p>
            </section>

            {/* ─── FIELD MAPPING TABLE ───────────────────────── */}
            <section className="mb-14">
                <h2 className="text-xl font-bold text-slate-900 mb-4">
                    {isEn ? 'Field Mapping: HL7 → JSON' : 'Mapeo de Campos: HL7 → JSON'}
                </h2>
                <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'TASY/HL7 Field' : 'Campo TASY/HL7'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'HL7 Segment' : 'Segmento HL7'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'JSON Field' : 'Campo JSON'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'Notes' : 'Notas'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-600">
                            <tr><td className="px-4 py-2.5">Patient ID</td><td className="px-4 py-2.5 font-mono text-xs">PID-3</td><td className="px-4 py-2.5 font-mono text-xs text-indigo-600">externalId</td><td className="px-4 py-2.5 text-xs">{isEn ? 'TASY internal patient number' : 'Número interno del paciente en TASY'}</td></tr>
                            <tr><td className="px-4 py-2.5">Patient Name</td><td className="px-4 py-2.5 font-mono text-xs">PID-5</td><td className="px-4 py-2.5 font-mono text-xs text-indigo-600">name</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Full name. Anonymized on public displays.' : 'Nombre completo. Se anonimiza en pantallas públicas.'}</td></tr>
                            <tr><td className="px-4 py-2.5">Attending Doctor</td><td className="px-4 py-2.5 font-mono text-xs">PV1-7</td><td className="px-4 py-2.5 font-mono text-xs text-indigo-600">doctorName</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Surgeon name' : 'Nombre del cirujano'}</td></tr>
                            <tr><td className="px-4 py-2.5">Procedure</td><td className="px-4 py-2.5 font-mono text-xs">PR1-3 / OBR-4</td><td className="px-4 py-2.5 font-mono text-xs text-indigo-600">procedureName</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Surgery description' : 'Descripción de la cirugía'}</td></tr>
                            <tr><td className="px-4 py-2.5">Location</td><td className="px-4 py-2.5 font-mono text-xs">PV1-3</td><td className="px-4 py-2.5 font-mono text-xs text-indigo-600">room</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Map to Hospital Status Room ID' : 'Mapear al Room ID de Hospital Status'}</td></tr>
                            <tr><td className="px-4 py-2.5">Admission Date</td><td className="px-4 py-2.5 font-mono text-xs">PV1-44</td><td className="px-4 py-2.5 font-mono text-xs text-indigo-600">admissionTime</td><td className="px-4 py-2.5 text-xs">ISO 8601</td></tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ─── QUICK START CODE ─────────────────────────── */}
            <section className="mb-14">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-violet-100 text-violet-700 p-2 rounded-lg"><Terminal className="w-5 h-5" /></span>
                    {isEn ? 'Middleware Code (Node.js)' : 'Código del Middleware (Node.js)'}
                </h2>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    {isEn
                        ? 'Drop-in Node.js middleware that listens for HL7 ADT messages from TASY and forwards them to Hospital Status. Save as middleware.js and run with Node 18+.' 
                        : 'Middleware Node.js listo para usar. Escucha mensajes HL7 ADT de TASY y los reenvía a Hospital Status. Guárdalo como middleware.js y ejecútalo con Node 18+.'}
                </p>

                <CodeBlock title="middleware.js — Node.js Adapter">{`// Hospital Status ↔ TASY Middleware
// Requirements: Node.js 18+ (native fetch)
// Install: npm init -y && npm install hl7-standard
// Run:     node middleware.js

const net = require('net');

// ─── CONFIGURATION ───────────────────────────────────────────
const CONFIG = {
  // Hospital Status API
  apiBaseUrl: '${baseUrl}/api/integration/v1',
  apiKeyId:   'hsk_YOUR_KEY_ID_HERE',
  apiSecret:  'hss_YOUR_SECRET_HERE',

  // HL7 Listener (TASY sends ADT messages here)
  hl7Port: 2575,

  // Room Mapping: TASY Location Code → Hospital Status Room ID
  roomMap: {
    'OR-1':   'room_abc123',    // Quirófano 1
    'OR-2':   'room_def456',    // Quirófano 2
    'PREOP':  null,             // Pre-op (no room assignment)
    'PACU':   null,             // Recovery (no room assignment)
  },

  // TASY Location → Hospital Status Status
  locationToStatus: {
    'PREOP':  'PREPARATION',
    'OR-1':   'ROOM_ENTRY',
    'OR-2':   'ROOM_ENTRY',
    'PACU':   'RECOVERY',
  },
};

// ─── API HELPER ──────────────────────────────────────────────
const apiHeaders = {
  'Content-Type': 'application/json',
  'Authorization': \`Bearer \${CONFIG.apiKeyId}:\${CONFIG.apiSecret}\`,
};

async function createPatient(data) {
  const res = await fetch(\`\${CONFIG.apiBaseUrl}/patients\`, {
    method: 'POST', headers: apiHeaders,
    body: JSON.stringify(data),
  });
  const json = await res.json();
  console.log('[CREATE]', res.status, json.patient?.id || json.error);
  return json;
}

async function updateStatus(patientId, status) {
  const res = await fetch(\`\${CONFIG.apiBaseUrl}/patients/\${patientId}/status\`, {
    method: 'PUT', headers: apiHeaders,
    body: JSON.stringify({ currentStatus: status }),
  });
  const json = await res.json();
  console.log('[UPDATE]', patientId, '→', status, res.status);
  return json;
}

// ─── HL7 PARSER (Minimal) ────────────────────────────────────
function parseHL7(raw) {
  const segments = raw.split('\\r').filter(Boolean);
  const msg = {};
  for (const seg of segments) {
    const fields = seg.split('|');
    const name = fields[0];
    msg[name] = fields;
  }
  return msg;
}

function getField(segment, index) {
  return segment && segment[index] ? segment[index].trim() : '';
}

// ─── MESSAGE HANDLER ─────────────────────────────────────────
async function handleHL7Message(raw) {
  const msg = parseHL7(raw);
  const msh = msg['MSH'];
  const evn = msg['EVN'];
  const pid = msg['PID'];
  const pv1 = msg['PV1'];

  if (!msh || !pid) return;

  const messageType = getField(msh, 9); // e.g., "ADT^A01"
  const eventCode = messageType.split('^')[1]; // e.g., "A01"
  const patientId = getField(pid, 3);   // TASY Patient ID
  const patientName = getField(pid, 5).replace('^', ' ');
  const doctor = getField(pv1, 7).replace('^', ' ');
  const location = getField(pv1, 3).split('^')[0];

  console.log(\`[HL7] \${messageType} | Patient: \${patientId} | Location: \${location}\`);

  switch (eventCode) {
    case 'A01': // Admission
      await createPatient({
        externalId: patientId,
        name: patientName,
        doctorName: doctor,
        room: CONFIG.roomMap[location] || null,
        procedureName: getField(msg['PR1'] || [], 3) || 'Procedimiento',
      });
      break;

    case 'A02': // Transfer
      const newStatus = CONFIG.locationToStatus[location];
      if (newStatus) {
        await updateStatus(patientId, newStatus);
      }
      break;

    case 'A03': // Discharge
      await updateStatus(patientId, 'EXIT');
      break;

    default:
      console.log(\`[HL7] Ignoring event \${eventCode}\`);
  }
}

// ─── TCP SERVER (HL7 MLLP Listener) ─────────────────────────
const server = net.createServer((socket) => {
  let buffer = '';
  socket.on('data', (data) => {
    buffer += data.toString();
    // MLLP: messages wrapped in \\x0B ... \\x1C\\x0D
    const start = buffer.indexOf('\\x0B');
    const end = buffer.indexOf('\\x1C\\x0D');
    if (start !== -1 && end !== -1) {
      const hl7Message = buffer.substring(start + 1, end);
      buffer = buffer.substring(end + 2);
      handleHL7Message(hl7Message)
        .then(() => {
          // Send ACK
          const ack = '\\x0BMSH|^~\\\\&|HS|HS|||' + new Date().toISOString() + '||ACK|1|P|2.5\\rMSA|AA|1\\x1C\\x0D';
          socket.write(ack);
        })
        .catch(err => {
          console.error('[ERROR]', err.message);
          const nak = '\\x0BMSH|^~\\\\&|HS|HS|||' + new Date().toISOString() + '||ACK|1|P|2.5\\rMSA|AE|1\\x1C\\x0D';
          socket.write(nak);
        });
    }
  });
});

server.listen(CONFIG.hl7Port, () => {
  console.log(\`🏥 Hospital Status Middleware listening on port \${CONFIG.hl7Port}\`);
  console.log(\`   API: \${CONFIG.apiBaseUrl}\`);
  console.log(\`   Ready to receive HL7 ADT messages from TASY\\n\`);
});`}</CodeBlock>
            </section>

            {/* ─── TESTING WITH CURL ─────────────────────────── */}
            <section className="mb-14">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-emerald-100 text-emerald-700 p-2 rounded-lg"><Zap className="w-5 h-5" /></span>
                    {isEn ? 'Quick Test with cURL' : 'Prueba Rápida con cURL'}
                </h2>
                <p className="text-sm text-slate-600 mb-4">
                    {isEn
                        ? 'Before configuring TASY, verify your API Key works by running these commands from any terminal:'
                        : 'Antes de configurar TASY, verifica que tu API Key funcione ejecutando estos comandos desde cualquier terminal:'}
                </p>

                <CodeBlock title={isEn ? '1. Health Check' : '1. Verificar Conexión'}>{`curl -s ${baseUrl}/api/integration/v1/ping \\
  -H "Authorization: Bearer hsk_YOUR_KEY:hss_YOUR_SECRET" | jq`}</CodeBlock>

                <CodeBlock title={isEn ? '2. Create Test Patient' : '2. Crear Paciente de Prueba'}>{`curl -s -X POST ${baseUrl}/api/integration/v1/patients \\
  -H "Authorization: Bearer hsk_YOUR_KEY:hss_YOUR_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "externalId": "TASY_TEST_001",
    "name": "Juan Pérez López",
    "procedureName": "Artroscopía de Rodilla",
    "doctorName": "Dr. García",
    "room": "YOUR_ROOM_ID"
  }' | jq`}</CodeBlock>

                <CodeBlock title={isEn ? '3. Update Status' : '3. Actualizar Status'}>{`curl -s -X PUT ${baseUrl}/api/integration/v1/patients/TASY_TEST_001/status \\
  -H "Authorization: Bearer hsk_YOUR_KEY:hss_YOUR_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{"currentStatus": "SURGERY", "surgeryProgress": 30}' | jq`}</CodeBlock>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4 flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-800">
                        {isEn
                            ? 'Note: You can look up patients by either the Hospital Status ID (e.g., "JP001") or the externalId you provided (e.g., "TASY_TEST_001"). Both work interchangeably.'
                            : 'Nota: Puedes buscar pacientes por el ID de Hospital Status (ej: "JP001") o por el externalId que proporcionaste (ej: "TASY_TEST_001"). Ambos funcionan indistintamente.'}
                    </p>
                </div>
            </section>

            {/* ─── WEBHOOK VERIFICATION ──────────────────────── */}
            <section className="mb-14">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-700 p-2 rounded-lg"><RefreshCw className="w-5 h-5" /></span>
                    {isEn ? 'Webhook Signature Verification' : 'Verificación de Firma del Webhook'}
                </h2>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    {isEn
                        ? 'When Hospital Status sends a webhook to your server, it includes an HMAC-SHA256 signature in the X-Signature-256 header. Verify it to ensure the request is authentic.'
                        : 'Cuando Hospital Status envía un webhook a tu servidor, incluye una firma HMAC-SHA256 en el header X-Signature-256. Verifícala para asegurar que el request es auténtico.'}
                </p>

                <CodeBlock title={isEn ? 'Webhook Receiver (Node.js)' : 'Receptor de Webhook (Node.js)'}>{`const http = require('http');
const crypto = require('crypto');

const WEBHOOK_SECRET = 'whs_YOUR_WEBHOOK_SECRET';

http.createServer((req, res) => {
  if (req.method !== 'POST') { res.end('OK'); return; }

  let body = '';
  req.on('data', chunk => body += chunk);
  req.on('end', () => {
    // Verify signature
    const expected = 'sha256=' + crypto
      .createHmac('sha256', WEBHOOK_SECRET)
      .update(body)
      .digest('hex');
    const received = req.headers['x-signature-256'];

    if (received !== expected) {
      console.error('⚠️  Invalid signature! Rejecting.');
      res.writeHead(401); res.end('Invalid signature');
      return;
    }

    const event = JSON.parse(body);
    console.log('✅ Webhook verified:', event.event);
    console.log('   Patient:', event.patient.id, '→', event.patient.currentStatus);
    console.log('   Timeline:', JSON.stringify(event.patient.timeline, null, 2));

    // TODO: Update TASY clinical record with timestamps
    res.writeHead(200); res.end('OK');
  });
}).listen(3001, () => console.log('Webhook receiver on :3001'));`}</CodeBlock>
            </section>

            {/* ─── TROUBLESHOOTING ───────────────────────────── */}
            <section className="mb-10">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                    {isEn ? 'Troubleshooting' : 'Solución de Problemas'}
                </h2>
                <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-4 py-3 font-semibold text-slate-900 w-24">Code</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'Error' : 'Error'}</th>
                                <th className="px-4 py-3 font-semibold text-slate-900">{isEn ? 'Solution' : 'Solución'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-600">
                            <tr><td className="px-4 py-2.5 font-mono text-xs font-bold text-red-600">401</td><td className="px-4 py-2.5 text-xs">API Key not found</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Check that the key ID and secret are correct. Secrets cannot be recovered — rotate if lost.' : 'Verifica que el Key ID y Secret sean correctos. Los Secrets no se pueden recuperar — rota si se perdió.'}</td></tr>
                            <tr><td className="px-4 py-2.5 font-mono text-xs font-bold text-red-600">403</td><td className="px-4 py-2.5 text-xs">IP not in whitelist</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Add your middleware server\'s public IP to the API Key whitelist in the Admin Panel.' : 'Agrega la IP pública de tu middleware al whitelist de la API Key en el Panel Admin.'}</td></tr>
                            <tr><td className="px-4 py-2.5 font-mono text-xs font-bold text-amber-600">404</td><td className="px-4 py-2.5 text-xs">Patient not found</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Ensure the patient was created first (A01) before updating status (A02). Use externalId for lookups.' : 'Asegúrate de haber creado el paciente (A01) antes de actualizar (A02). Usa externalId para búsquedas.'}</td></tr>
                            <tr><td className="px-4 py-2.5 font-mono text-xs font-bold text-amber-600">429</td><td className="px-4 py-2.5 text-xs">Rate limit exceeded</td><td className="px-4 py-2.5 text-xs">{isEn ? 'Default: 100 req/min. Contact your admin to increase or batch your requests.' : 'Default: 100 req/min. Contacta al admin para aumentar o agrupa tus requests.'}</td></tr>
                            <tr><td className="px-4 py-2.5 font-mono text-xs font-bold text-slate-500">Webhook disabled</td><td className="px-4 py-2.5 text-xs">Circuit breaker tripped</td><td className="px-4 py-2.5 text-xs">{isEn ? 'After 5 consecutive delivery failures, webhooks auto-disable. Fix the receiving endpoint, then re-enable from Admin Panel.' : 'Después de 5 fallos consecutivos, los webhooks se desactivan. Arregla el endpoint receptor y luego reactívalos desde el Panel Admin.'}</td></tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ─── SANDBOX MODE ──────────────────────────────── */}
            <section className="mb-10">
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                    <h3 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        {isEn ? 'Sandbox Mode' : 'Modo Sandbox'}
                    </h3>
                    <p className="text-sm text-indigo-800 leading-relaxed">
                        {isEn
                            ? 'Create an API Key with "Sandbox" mode enabled to safely test your integration. Sandbox patients are prefixed with TEST_ and auto-delete after 5 minutes. They appear on the staff dashboard but with a visible [TEST] indicator, so they won\'t interfere with real operations.'
                            : 'Crea una API Key con modo "Sandbox" activado para probar tu integración de forma segura. Los pacientes sandbox tienen prefijo TEST_ y se auto-eliminan a los 5 minutos. Aparecen en el dashboard del staff con un indicador [TEST] visible, sin interferir con operaciones reales.'}
                    </p>
                </div>
            </section>
        </div>
    );
}
